// shared/scoring.ts — road analysis: sampling, profile solver, velocity profile, scoring.
// Shared between server (authoritative) and client (live UI feedback).

import { heightAt, isWater, HEIGHT_SCALE_KM, WATER_THRESHOLD, WU_TO_KM } from "./terrain.js";
import type { Puzzle, TerrainParams, River, Lake } from "./terrain.js";

export const MAX_GRADE = 0.07;
// export const Z_TOLERANCE = 0.003;
export const Z_TOLERANCE = 0.001;
export const SAMPLE_SPACING_KM = 0.4;
export const DENSE_T_PER_SEGMENT = 80;

// Bridge clearance above water surface — road sits this far above
// WATER_THRESHOLD elevation wherever it crosses water.
export const BRIDGE_CLEARANCE_KM = 0.005;  // 5 m above water surface

// Depth (road below terrain) at which a bored tunnel becomes the chosen
// construction method instead of an open cut. Below this, the cut is
// shallow enough to excavate from above and leave open ("cutting" in road
// engineering parlance). Above this depth the cut would be too tall to
// retain safely, so we model a bored tunnel.
//
// Anything between Z_TOLERANCE (3 m, the at-grade threshold) and
// TUNNEL_DEPTH_THRESHOLD (8 m) is classified as opencut.
export const TUNNEL_DEPTH_THRESHOLD = 0.0125;  // 12.5 m

/**
 * Height (road above terrain) at which a structure becomes cheaper to build
 * as a span than as an earth embankment. Below this the road stands on fill;
 * above it, on piers.
 *
 * The mirror of TUNNEL_DEPTH_THRESHOLD, and it exists for the same reason.
 * Before this, EVERY deviation above the at-grade band was a "bridge" priced
 * at a flat 15/km — so a 1.1 m embankment and a 200 m viaduct cost the same,
 * and the model had no way to express the most ordinary thing a road does
 * with a small rise. Two visible consequences:
 *
 *   Cutting beat filling by 7.5x at the margin. One millimetre below the
 *   at-grade band cost 2/km; one millimetre above cost 15/km. Wherever the
 *   ruling grade forced the road off the ground it went DOWN, which is the
 *   "unnecessary open cut where a surface road would do" symptom.
 *
 *   Low fills were classified as bridges. Measured over 16 roads on 4 days,
 *   48.6% of all segments came back "bridge", nearly all of them hugging the
 *   terrain a few metres up. The profile strip draws a bridge run as a
 *   straight deck from abutment to abutment, so a "bridge" that follows a
 *   hill renders as a deck ploughing through it — 179 of 217 multi-segment
 *   bridge runs did exactly that, the worst 272 m below ground.
 */
export const FILL_HEIGHT_THRESHOLD = 0.0125;  // 12.5 m

export const BASE_COST_PER_KM = 1.2;
export const BRIDGE_COST_PER_KM = 15;
export const TUNNEL_COST_PER_KM = 25;
// Open cut: vertical/near-vertical excavation, no roof. Per real-world
// numbers an open cut runs ~20-40% the cost of a bored tunnel at similar
// depths. Tuned here to make opencut a meaningfully different option
// rather than a niche edge case — it should dominate tunneling in the
// 3-8 m depth range and lose to it as depth grows further.
//
// Priced by excavated volume, not by length: a per-km rate charges the same
// for a 1 m scrape as for a 12 m trench. (A per-km OPENCUT_COST_PER_KM = 9
// used to sit alongside this one, unreferenced, and is now gone.)
export const OPENCUT_COST_PER_M3 = 2;
/**
 * Embankment, per m of height per km. Cheaper than excavation of the same
 * size — fill is placed rather than cut and hauled — and set so that fill
 * meets the bridge rate exactly at FILL_HEIGHT_THRESHOLD, the way opencut
 * meets the tunnel rate at TUNNEL_DEPTH_THRESHOLD. No discontinuity at
 * either threshold means the solver never gets a free jump by stepping
 * across one.
 */
export const FILL_COST_PER_M3 = BRIDGE_COST_PER_KM / (FILL_HEIGHT_THRESHOLD * 1000);

export const CRUISE_AT_GRADE_KMH = 100;
export const CRUISE_BRIDGE_KMH = 90;
export const CRUISE_TUNNEL_KMH = 80;
// Open cuts are essentially open-air roads with retaining walls; speed
// isn't constrained by tunnel ventilation or psychology. Treat as at-grade.
export const CRUISE_OPENCUT_KMH = 100;
// Lateral acceleration limit for cornering: gameplay-tuned to be ~10x more
// conservative than real-world road design (AASHTO uses 0.10–0.17g, this is
// effectively ~0.012g). Tuned so curves feel meaningfully expensive at the
// scale of our maps. Pair with the standard km->m radius conversion.
export const A_LAT_MPS2 = 0.12;
export const A_ACCEL_MPS2 = 1.0;
// Braking is also gameplay-softened (real design comfort is ~1.5–2.0 m/s²)
// so that the dip in speed around corners has a visible runway in both
// directions.
export const A_BRAKE_MPS2 = 0.9;

export type Pt = { x: number; y: number };
export type Handles = { in: Pt | null; out: Pt | null };
export type Road = { anchors: Pt[]; handles: Handles[] };
export type Sample = { x: number; y: number; s: number; zT: number; overWater: boolean };
export type Kind = "grade" | "opencut" | "fill" | "bridge" | "tunnel";
export type Score = {
  length: number; lenGrade: number; lenOpencut: number; lenFill: number; lenBridge: number; lenTunnel: number; costGrade: number; costOpencut: number; costFill: number; costBridge: number; costTunnel: number;
  cost: number; timeMin: number; minSpeed: number;
};
export type Analysis = {
  samples: Sample[]; z: number[]; kinds: Kind[]; speeds: number[];
  score: Score; feasible: boolean;
  /** Straight deck spans, abutment to abutment. See bridgeDeckSpans. */
  deckSpans: DeckSpan[];
  // Arc positions where the structure kind changes: tunnel portals, bridge
  // abutments, at-grade transitions. For rendering portal markers.
  portals: number[];
};

export function cubicPoint(p0: Pt, c1: Pt, c2: Pt, p1: Pt, t: number): Pt {
  const u = 1 - t, uu = u * u, uuu = uu * u;
  const tt = t * t, ttt = tt * t;
  return {
    x: uuu * p0.x + 3 * uu * t * c1.x + 3 * u * tt * c2.x + ttt * p1.x,
    y: uuu * p0.y + 3 * uu * t * c1.y + 3 * u * tt * c2.y + ttt * p1.y,
  };
}

export function buildSampledRoad(road: Road, params: TerrainParams): Sample[] | null {
  if (road.anchors.length < 2) return null;
  const dense: Pt[] = [];
  for (let i = 0; i < road.anchors.length - 1; i++) {
    const p0 = road.anchors[i], p1 = road.anchors[i + 1];
    const c1 = road.handles[i].out, c2 = road.handles[i + 1].in;
    if (!c1 || !c2) continue;
    const start = i === 0 ? 0 : 1;
    for (let k = start; k <= DENSE_T_PER_SEGMENT; k++) {
      dense.push(cubicPoint(p0, c1, c2, p1, k / DENSE_T_PER_SEGMENT));
    }
  }
  if (dense.length < 2) return null;
  // Cumulative arc length in *kilometers*. Coordinate-space deltas come
  // out in world units; we apply WU_TO_KM here so the rest of the scoring
  // pipeline can treat sample.s as a physical distance.
  const cum = [0];
  for (let i = 1; i < dense.length; i++) {
    const dxWu = dense[i].x - dense[i - 1].x;
    const dyWu = dense[i].y - dense[i - 1].y;
    cum.push(cum[i - 1] + Math.hypot(dxWu, dyWu) * WU_TO_KM);
  }
  const totalLen = cum[cum.length - 1];   // in km
  if (totalLen < 1e-6) return null;
  const nSamples = Math.max(2, Math.ceil(totalLen / SAMPLE_SPACING_KM) + 1);
  const samples: Sample[] = [];
  let j = 0;
  for (let i = 0; i < nSamples; i++) {
    const targetS = i === nSamples - 1 ? totalLen : (i * totalLen) / (nSamples - 1);
    while (j < cum.length - 2 && cum[j + 1] < targetS) j++;
    const segS0 = cum[j], segS1 = cum[j + 1];
    const t = segS1 > segS0 ? (targetS - segS0) / (segS1 - segS0) : 0;
    const x = dense[j].x + (dense[j + 1].x - dense[j].x) * t;
    const y = dense[j].y + (dense[j + 1].y - dense[j].y) * t;
    samples.push({
      x, y, s: targetS,
      zT: heightAt(x, y, params) * HEIGHT_SCALE_KM,
      overWater: isWater(x, y, params),
    });
  }
  return samples;
}

export function solveProfile(samples: Sample[]): { z: number[]; feasible: boolean } {
  const N = samples.length;
  if (N < 2) return { z: samples.map((p) => p.zT), feasible: true };

  // Effective target elevation: terrain everywhere except over water,
  // where it's lifted to the bridge surface so the road sits above the water.
  const waterSurface = WATER_THRESHOLD * HEIGHT_SCALE_KM + BRIDGE_CLEARANCE_KM;
  const target = samples.map((p) => p.overWater ? Math.max(p.zT, waterSurface) : p.zT);

  const zFwd = new Array(N);
  zFwd[0] = target[0];
  for (let i = 1; i < N; i++) {
    const ds = samples[i].s - samples[i - 1].s;
    const maxStep = MAX_GRADE * ds;
    zFwd[i] = Math.max(zFwd[i - 1] - maxStep, Math.min(zFwd[i - 1] + maxStep, target[i]));
  }
  const zBack = new Array(N);
  zBack[N - 1] = target[N - 1];
  for (let i = N - 2; i >= 0; i--) {
    const ds = samples[i + 1].s - samples[i].s;
    const maxStep = MAX_GRADE * ds;
    zBack[i] = Math.max(zBack[i + 1] - maxStep, Math.min(zBack[i + 1] + maxStep, target[i]));
  }
  const z = new Array(N);
  for (let i = 0; i < N; i++) {
    const t = target[i];
    z[i] = Math.abs(zFwd[i] - t) < Math.abs(zBack[i] - t) ? zFwd[i] : zBack[i];
  }
  for (let i = 1; i < N; i++) {
    const ds = samples[i].s - samples[i - 1].s;
    const maxStep = MAX_GRADE * ds;
    z[i] = Math.max(z[i - 1] - maxStep, Math.min(z[i - 1] + maxStep, z[i]));
  }
  const sEnd = samples[N - 1].s;
  const elevDiff = Math.abs(target[N - 1] - target[0]);
  const feasible = elevDiff <= MAX_GRADE * sEnd + 1e-6;
  return { z, feasible };
}

export function classifySegments(
  samples: Sample[],
  z: number[],
  forceBridge?: boolean[],
): Kind[] {
  const N = samples.length;
  const kinds: Kind[] = new Array(N - 1);
  for (let i = 0; i < N - 1; i++) {
    // Hard override: segments crossing a river or lake (computed
    // geometrically) are always bridges, regardless of profile elevation.
    if (forceBridge && forceBridge[i]) {
      kinds[i] = "bridge";
      continue;
    }
    // Ocean crossings (heightmap below WATER_THRESHOLD) are also always
    // bridges — handled here via `overWater` for backward compatibility.
    if (samples[i].overWater || samples[i + 1].overWater) {
      kinds[i] = "bridge";
      continue;
    }
    // diff > 0: road is above terrain (bridge / fill)
    // diff < 0: road is below terrain (cut)
    const diff = (z[i] + z[i + 1]) / 2 - (samples[i].zT + samples[i + 1].zT) / 2;
    if (diff > FILL_HEIGHT_THRESHOLD) {
      // High enough that piers beat hauling in the earth to stand on.
      kinds[i] = "bridge";
    } else if (diff > Z_TOLERANCE) {
      // Above the at-grade band but low enough to sit on an embankment.
      kinds[i] = "fill";
    } else if (diff < -TUNNEL_DEPTH_THRESHOLD) {
      // Deep enough that a bored tunnel is more economical than open excavation.
      kinds[i] = "tunnel";
    } else if (diff < -Z_TOLERANCE) {
      // Below terrain by more than the at-grade tolerance but shallow enough
      // for an open cut from the surface.
      kinds[i] = "opencut";
    } else {
      kinds[i] = "grade";
    }
  }
  return kinds;
}

// =============================================================
// GEOMETRIC WATER CROSSINGS
// =============================================================
// Rivers and lakes aren't water-tile features in this game — they're
// geometric objects (polylines and polygons) that force the road segments
// crossing them to be bridges. Per-segment crossing tests run after the
// road is sampled, and override the natural grade/cut/bridge/tunnel
// classification with "bridge" wherever the road actually crosses water.

/**
 * 2D segment-segment intersection test (excluding collinear overlap).
 * Returns true if segments AB and CD properly intersect (i.e. cross each
 * other with non-degenerate orientation). Endpoint contact counts as a
 * crossing.
 */
function segmentsIntersect(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): boolean {
  const d1x = bx - ax, d1y = by - ay;
  const d2x = dx - cx, d2y = dy - cy;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-12) return false;   // parallel; treat as non-crossing
  const tNum = (cx - ax) * d2y - (cy - ay) * d2x;
  const uNum = (cx - ax) * d1y - (cy - ay) * d1x;
  const t = tNum / denom;
  const u = uNum / denom;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

/**
 * Point-in-polygon test (even-odd rule). Polygon is a closed polyline
 * (first === last point). Returns true if (px, py) is strictly inside the
 * polygon boundary.
 */
function pointInPolygon(px: number, py: number, loop: Array<{ x: number; y: number }>): boolean {
  let inside = false;
  const n = loop.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = loop[i].x, yi = loop[i].y;
    const xj = loop[j].x, yj = loop[j].y;
    const crosses = ((yi > py) !== (yj > py)) &&
      (px < ((xj - xi) * (py - yi)) / (yj - yi) + xi);
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * Point-in-lake using the even-odd rule across ALL outline loops. With
 * multiple loops, the outer + island holes naturally produce the correct
 * "in lake but not in island" result.
 */
function pointInLake(px: number, py: number, lake: Lake): boolean {
  let inside = false;
  for (const loop of lake.outline) {
    if (pointInPolygon(px, py, loop)) inside = !inside;
  }
  return inside;
}

/**
 * For each consecutive pair of samples, return true if the segment
 * geometrically crosses a river polyline or enters/exits/contains a lake.
 * Per-segment cost is O(rivers' total edges + lakes' total edges + lakes).
 * Skip-test via bounding box for each river/lake to keep this cheap on
 * roads that mostly stay clear of water.
 */
export function computeWaterCrossings(
  samples: Sample[],
  rivers: River[],
  lakes: Lake[],
): boolean[] {
  const N = samples.length;
  const out: boolean[] = new Array(N - 1).fill(false);
  if (N < 2 || (rivers.length === 0 && lakes.length === 0)) return out;

  // Precompute per-river/per-lake bounding boxes for early-out.
  type BBox = { x0: number; y0: number; x1: number; y1: number };
  function loopBBox(pts: Array<{ x: number; y: number }>): BBox {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of pts) {
      if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x;
      if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y;
    }
    return { x0, y0, x1, y1 };
  }
  const riverBBox = rivers.map(r => loopBBox(r.points));
  const lakeOutlineBBoxes = lakes.map(l => l.outline.map(loop => loopBBox(loop)));

  for (let i = 0; i < N - 1; i++) {
    const a = samples[i], b = samples[i + 1];
    const sx0 = Math.min(a.x, b.x), sx1 = Math.max(a.x, b.x);
    const sy0 = Math.min(a.y, b.y), sy1 = Math.max(a.y, b.y);

    // --- Rivers: segment-segment intersection. ---
    let bridged = false;
    for (let ri = 0; ri < rivers.length && !bridged; ri++) {
      const bb = riverBBox[ri];
      // Bounding-box prune.
      if (sx1 < bb.x0 || sx0 > bb.x1 || sy1 < bb.y0 || sy0 > bb.y1) continue;
      const pts = rivers[ri].points;
      for (let k = 0; k < pts.length - 1; k++) {
        if (segmentsIntersect(a.x, a.y, b.x, b.y, pts[k].x, pts[k].y, pts[k + 1].x, pts[k + 1].y)) {
          bridged = true; break;
        }
      }
    }
    if (bridged) { out[i] = true; continue; }

    // --- Lakes: outline-crossing OR endpoint-inside. ---
    // A segment is "over a lake" if EITHER it crosses an outline loop
    // (entering/exiting) OR an endpoint is inside the lake (interior
    // segment, possible if both endpoints are inside).
    for (let li = 0; li < lakes.length && !bridged; li++) {
      // Aggregate bbox: skip lake if the segment is entirely outside.
      let lakeMinX = Infinity, lakeMinY = Infinity, lakeMaxX = -Infinity, lakeMaxY = -Infinity;
      for (const lb of lakeOutlineBBoxes[li]) {
        if (lb.x0 < lakeMinX) lakeMinX = lb.x0;
        if (lb.y0 < lakeMinY) lakeMinY = lb.y0;
        if (lb.x1 > lakeMaxX) lakeMaxX = lb.x1;
        if (lb.y1 > lakeMaxY) lakeMaxY = lb.y1;
      }
      if (sx1 < lakeMinX || sx0 > lakeMaxX || sy1 < lakeMinY || sy0 > lakeMaxY) continue;
      const lake = lakes[li];
      // Crossing tests against outline edges.
      for (let lo = 0; lo < lake.outline.length && !bridged; lo++) {
        const loop = lake.outline[lo];
        for (let k = 0; k < loop.length - 1; k++) {
          if (segmentsIntersect(a.x, a.y, b.x, b.y, loop[k].x, loop[k].y, loop[k + 1].x, loop[k + 1].y)) {
            bridged = true; break;
          }
        }
      }
      // Endpoint-inside test (catches segments entirely within a lake).
      if (!bridged && (pointInLake(a.x, a.y, lake) || pointInLake(b.x, b.y, lake))) {
        bridged = true;
      }
    }
    if (bridged) out[i] = true;
  }
  return out;
}

/**
 * For each sample inside a lake polygon, override its `zT` to the lake's
 * surface elevation (in km). This lets `solveProfile` route the road
 * profile across the lake surface rather than dipping into the basin
 * floor, which would produce nonsense grades and microscopic bridges
 * visually.
 */
export function liftSamplesForLakes(
  samples: Sample[],
  lakes: Lake[],
): void {
  if (lakes.length === 0) return;
  for (const s of samples) {
    for (const lake of lakes) {
      if (pointInLake(s.x, s.y, lake)) {
        const lakeZ = lake.surface * HEIGHT_SCALE_KM;
        if (lakeZ > s.zT) s.zT = lakeZ;
        break;   // a point can be in at most one lake (lakes don't overlap)
      }
    }
  }
}

function computeRadii(samples: Sample[]): number[] {
  const N = samples.length;
  const r = new Array(N).fill(Infinity);
  if (N < 3) return r;
  for (let i = 1; i < N - 1; i++) {
    const a = samples[i - 1], b = samples[i], c = samples[i + 1];
    const ab = Math.hypot(b.x - a.x, b.y - a.y);
    const bc = Math.hypot(c.x - b.x, c.y - b.y);
    const ac = Math.hypot(c.x - a.x, c.y - a.y);
    const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    const area2 = Math.abs(cross);
    if (area2 < 1e-9 || ab * bc < 1e-9) { r[i] = Infinity; continue; }
    r[i] = (ab * bc * ac) / (2 * area2);
  }
  r[0] = r[1];
  r[N - 1] = r[N - 2];
  return r;
}

export function computeVelocityProfile(samples: Sample[], kinds: Kind[]): number[] {
  const N = samples.length;
  if (N < 2) return new Array(N).fill(CRUISE_AT_GRADE_KMH);
  const radii = computeRadii(samples);
  function cruise(kind: Kind | null): number {
    if (kind === "bridge") return CRUISE_BRIDGE_KMH;
    if (kind === "tunnel") return CRUISE_TUNNEL_KMH;
    if (kind === "opencut") return CRUISE_OPENCUT_KMH;
    // "fill" falls through with "grade": an embankment is an ordinary road
    // that happens to stand on placed earth. Nothing about it limits speed.
    return CRUISE_AT_GRADE_KMH;
  }
  function cruiseForSample(i: number): number {
    const k1 = i > 0 ? kinds[i - 1] : null;
    const k2 = i < N - 1 ? kinds[i] : null;
    if (k1 && k2) return Math.min(cruise(k1), cruise(k2));
    return cruise(k1 ?? k2);
  }
  const v = new Array(N);
  for (let i = 0; i < N; i++) {
    const cruiseMps = cruiseForSample(i) / 3.6;
    // radii[] is in world units; convert wu -> km -> m.
    const radiusM = radii[i] * WU_TO_KM * 1000;
    v[i] = Math.min(cruiseMps, Math.sqrt(A_LAT_MPS2 * radiusM));
  }
  v[0] = 0;
  v[N - 1] = 0;
  for (let i = 0; i < N - 1; i++) {
    const dsM = (samples[i + 1].s - samples[i].s) * 1000;
    v[i + 1] = Math.min(v[i + 1], Math.sqrt(v[i] * v[i] + 2 * A_ACCEL_MPS2 * dsM));
  }
  for (let i = N - 2; i >= 0; i--) {
    const dsM = (samples[i + 1].s - samples[i].s) * 1000;
    v[i] = Math.min(v[i], Math.sqrt(v[i + 1] * v[i + 1] + 2 * A_BRAKE_MPS2 * dsM));
  }
  return v.map((vm) => vm * 3.6);
}

export function scoreRoute(samples: Sample[], z: number[], kinds: Kind[], speeds: number[]): Score | null {
  const N = samples.length;
  if (!samples || N < 2) return null;
  let length = 0, lenGrade = 0, lenOpencut = 0, lenFill = 0, lenBridge = 0, lenTunnel = 0;
  let costGrade = 0, costOpencut = 0, costFill = 0, costBridge = 0, costTunnel = 0;
  let cost = 0, timeHours = 0, minSpeed = Infinity;
  for (let i = 1; i < N; i++) {
    const ds = samples[i].s - samples[i - 1].s;
    if (ds < 1e-6) continue;
    const kind = kinds[i - 1];
    let cpk;
    if (kind === "bridge") {
        cpk = BRIDGE_COST_PER_KM; lenBridge += ds;
        costBridge += cpk * ds;
    }
    else if (kind === "tunnel") {
        cpk = TUNNEL_COST_PER_KM; lenTunnel += ds;
        costTunnel += cpk * ds;
    }
    else if (kind === "opencut") {
        // Mean depth over the segment, in METRES.
        //
        // Was: OPENCUT_COST_PER_M3 * (samples[i].zT - z[i]) — the RIGHT
        // ENDPOINT depth, in KM. Two separate defects, both severe:
        //
        //   Units. At 2 per m³ with depth in km, a 5 m cut billed 0.01/km
        //   against 1.2/km for plain road — an open cut was 120x CHEAPER
        //   than just building the road on the ground, and a 1 m scrape was
        //   600x cheaper. Any search minimising this number (par.ts, and the
        //   player optimising by eye) is pushed toward trenching the whole
        //   route. The DP already used metres via OPENCUT_DEPTH_UNITS; only
        //   scoreRoute was left in km, so the two disagreed by 1000x on
        //   exactly the decision they were meant to agree on.
        //
        //   Endpoint vs mean. classifySegments decides the kind from the
        //   segment MEAN but this billed the right endpoint, so a segment
        //   crossing the ground line inside itself could be classified
        //   opencut and then billed a NEGATIVE cost. The DP's header comment
        //   flagged this years ago and corrected it on its own side; the fix
        //   never landed here.
        const depthKm = (samples[i - 1].zT + samples[i].zT) * 0.5
                      - (z[i - 1] + z[i]) * 0.5;
        cpk = OPENCUT_COST_PER_M3 * Math.max(0, depthKm) * 1000;
        lenOpencut += ds;
        costOpencut += cpk * ds;
    }
    else if (kind === "fill") {
        // Symmetric with opencut: mean height, in metres.
        const heightKm = (z[i - 1] + z[i]) * 0.5
                       - (samples[i - 1].zT + samples[i].zT) * 0.5;
        cpk = FILL_COST_PER_M3 * Math.max(0, heightKm) * 1000;
        lenFill += ds;
        costFill += cpk * ds;
    }
    else {
        cpk = BASE_COST_PER_KM; lenGrade += ds;
        costGrade += cpk * ds;
    }

    cost += cpk * ds;
    const v1 = Math.max(5, speeds[i - 1]);
    const v2 = Math.max(5, speeds[i]);
    timeHours += 0.5 * (ds / v1 + ds / v2);
    if (i != 1 && i != N-1) {
        minSpeed = Math.min(minSpeed, v1, v2);
    }
    length += ds;
  }
  return {
    length, lenGrade, lenOpencut, lenFill, lenBridge, lenTunnel,
    costGrade, costOpencut, costFill, costBridge, costTunnel,
    cost, timeMin: timeHours * 60,
    minSpeed: isFinite(minSpeed) ? minSpeed : 0,
  };
}

// =============================================================
// OPTIMAL PROFILE SOLVER (dynamic programming / Pontryagin)
// =============================================================
// Replaces iterative descent with a single forward sweep that finds the
// GLOBAL optimum on an elevation lattice — no initial guess, no iteration,
// no dependence on solveProfile's alignment.
//
// Why a lattice is legitimate. Treat the profile as optimal control: state
// z, control u = z' constrained to |u| <= MAX_GRADE, cost ∫ c(z − zT) ds.
// The Hamiltonian H = c(z − zT) + λu is LINEAR in u, so Pontryagin's
// principle gives bang-bang control: u = −MAX_GRADE·sign(λ), with λ' =
// −c'(δ). Optimal profiles are therefore concatenations of terrain-hugging
// arcs and ±MAX_GRADE arcs with finitely many switch points — exactly the
// structure a discrete elevation grid represents. The ruling-grade
// constraint makes the problem EASIER, not harder: it collapses an
// infinite-dimensional variational problem to a finite one.
//
// Why DP rather than descent. scoreRoute's cost is a sum over segments in
// which each term depends only on (z_i, z_{i+1}), and the grade limit is a
// local constraint on the same pair. That is a chain-structured objective,
// so Bellman applies: sweeping i once, keeping the best cost-to-reach for
// every lattice elevation, yields the global optimum. Cost is exact — the
// DP's per-transition cost IS scoreRoute's per-segment cost, not a
// surrogate. Descent could only ever find a local optimum near wherever it
// started, which is why it depended so much on the initial classification.
//
// Complexity. Work is N·M·K, where M is lattice height and K = 2·MAX_GRADE·
// Δs/Δz is the transition fan-out. Since N·K = 2·MAX_GRADE·L/Δz, total work
// is independent of arc-length resolution — it scales with relief and
// elevation resolution only. The sweep runs at full resolution in one pass;
// reachability windows and branch-and-bound against the status-quo profile
// keep it to a few milliseconds WITHOUT restricting the search, so the
// result is the true lattice optimum rather than a polish of a coarse
// guess.
//
// COST MODEL. The DP minimizes scoreRoute's densities with two deliberate
// corrections; scoreRoute itself is unmodified.
//
//   1. Opencut depth is measured as the SEGMENT MEAN (−diff), not the
//      right-endpoint depth (samples[i].zT − z[i]) that scoreRoute uses.
//      scoreRoute classifies on the mean but bills on the endpoint; where
//      the profile crosses terrain inside a segment these disagree in sign,
//      so a segment classified as opencut can be billed a NEGATIVE cost.
//      Optimizing that literally drives total cost below zero.
//   2. Segment cost is INTEGRATED across the segment instead of billed by
//      the segment's mean deviation. classifySegments decides a kind from
//      the mean, so a segment that is half bridge and half tunnel averages
//      to roughly zero and is billed as flat road; optimizing that produces
//      a road weaving through the ground at at-grade prices.
//   3. Opencut depth is converted to METERS (OPENCUT_DEPTH_UNITS = "m").
//      In scoreRoute's km units the opencut density is 0.002–0.025 per km —
//      about 100x cheaper than at-grade road (1.2), so the true global
//      optimum is a shallow trench along the entire route. Earlier
//      optimizers never surfaced this because their parametrization pinned
//      the road to the terrain; a from-scratch solver has no such blinkers.
//
// Set OPENCUT_DEPTH_UNITS = "km" and OPENCUT_MEAN_DEPTH = false to make the
// DP minimize scoreRoute exactly as written (and watch it trench).

/** "m" keeps opencut between at-grade and tunnel cost; "km" reproduces
 *  scoreRoute's literal units, under which trenching dominates. */
const OPENCUT_DEPTH_UNITS: "km" | "m" = "m";
/**
 * Infinitesimal preference for hugging the ground, in cost per km per km of
 * |z − zT|. At-grade cost is FLAT across the whole dead band |δ| <=
 * Z_TOLERANCE, so without this every profile staying within a metre of the
 * terrain costs exactly the same and the DP tie-breaks arbitrarily among
 * thousands of equal-cost lattice paths — which renders as a road visibly
 * hunting up and down around the ground line. This term is far too small to
 * change any structural decision (a 300 m deep tunnel over 30 km picks up
 * <0.01 cost, against totals in the hundreds) but is many orders of
 * magnitude larger than floating-point noise, so it decides ties cleanly.
 * It also picks the shallowest tunnel and the lowest bridge deck among
 * otherwise equal options, which is what you'd want anyway.
 */
const HUG_EPSILON = 1e-3;
/** Bill opencut on the segment mean depth (consistent with how the segment
 *  is classified) rather than scoreRoute's right-endpoint depth. */
const OPENCUT_MEAN_DEPTH = true;

export type ProfileSolverOptions = {
  /** Elevation lattice resolution in km (default 0.0005 = 0.5 m). */
  dzKm?: number;
  /** Cap on lattice height; dz is coarsened if relief needs more (default 1600). */
  maxLevels?: number;
  /** Coarsest pitch the budget may choose, in km (default 0.001 = 1 m). */
  dzMaxKm?: number;
  /** Target DP transitions; drives the adaptive pitch (default 1.5e6). */
  transitionBudget?: number;
};

export type ProfileSolution = {
  /** Optimal road elevation at each sample (km). */
  z: number[];
  /** Cost of that profile under the DP's model (not scoreRoute's number). */
  cost: number;
  /** Lattice resolution actually used (km). */
  dzKm: number;
};

/**
 * Globally optimal road profile for the given samples, by a SINGLE
 * full-resolution DP pass over an elevation lattice.
 *
 * An earlier version solved a coarse lattice first and then refined inside a
 * narrow tube around that answer. That was a mistake: the coarse lattice
 * (relief/191, ~2.6 m on a 500 m map) is too coarse to represent the ±1 m
 * at-grade band or to place the 12.5 m tunnel threshold, so the structural
 * choices — bridge vs shallow cut vs tunnel, and where a portal sits — were
 * decided at a resolution that cannot distinguish them, and refinement could
 * only polish within ±5 m of that distorted answer. It made a global method
 * behave like a local search from a bad start. There is no tube here; every
 * lattice elevation is a live candidate at every sample.
 *
 * Affordability comes from exact pruning instead, so optimality is
 * preserved:
 *   - Reachability: at each sample, elevations outside the ruling-grade
 *     cones of the two pinned endpoints, or outside the terrain's own
 *     elevation range, cannot appear in any feasible profile.
 *   - Branch and bound: with `seedZ` (normally solveProfile's alignment) as
 *     an incumbent upper bound, a partial profile is discarded when its
 *     cost so far plus the cheapest conceivable cost for the remaining
 *     distance already exceeds the incumbent. The bound uses the minimum
 *     cost density over all kinds, so it never discards an optimum.
 */
export function solveProfileOptimal(
  samples: Sample[],
  forceBridge?: boolean[],
  options: ProfileSolverOptions = {},
  seedZ?: number[],
): ProfileSolution | null {
  const N = samples.length;
  if (N < 2) return null;
  const dzWanted = options.dzKm ?? 0.0005;
  const maxLevels = options.maxLevels ?? 1600;

  const S = samples.map((p) => p.s);
  const ZT = samples.map((p) => p.zT);
  const waterSurface = WATER_THRESHOLD * HEIGHT_SCALE_KM + BRIDGE_CLEARANCE_KM;
  const T = samples.map((p) => (p.overWater ? Math.max(p.zT, waterSurface) : p.zT));
  const forcedSeg: boolean[] = new Array(N - 1);
  for (let i = 0; i < N - 1; i++) {
    forcedSeg[i] = (forceBridge ? forceBridge[i] : false) ||
      samples[i].overWater || samples[i + 1].overWater;
  }
  const opencutRate = OPENCUT_DEPTH_UNITS === "m"
    ? OPENCUT_COST_PER_M3 * 1000
    : OPENCUT_COST_PER_M3;
  // Same unit convention as opencut; see OPENCUT_DEPTH_UNITS.
  const fillRate = OPENCUT_DEPTH_UNITS === "m"
    ? FILL_COST_PER_M3 * 1000
    : FILL_COST_PER_M3;

  let zLo = Infinity, zHi = -Infinity;
  for (let i = 0; i < N; i++) {
    if (T[i] < zLo) zLo = T[i];
    if (T[i] > zHi) zHi = T[i];
  }
  // A profile above the highest ground or below the lowest is never optimal:
  // both only lengthen whatever structure the road is riding on.
  const relief = Math.max(1e-6, zHi - zLo);

  /** Cost density at deviation d (road minus terrain), in cost per km. */
  function density(d: number): number {
    if (d > FILL_HEIGHT_THRESHOLD) return BRIDGE_COST_PER_KM;
    if (d > Z_TOLERANCE) return fillRate * d;
    if (d < -TUNNEL_DEPTH_THRESHOLD) return TUNNEL_COST_PER_KM;
    if (d < -Z_TOLERANCE) return opencutRate * -d;
    return BASE_COST_PER_KM;
  }

  // Antiderivative of the density. Deviation is linear across a segment, so
  // substituting u = d(t) turns the segment integral into a difference:
  //
  //     ∫₀¹ density(d₁ + t·(d₂−d₁)) dt = (Φ(d₂) − Φ(d₁)) / (d₂ − d₁)
  //
  // That is the exact same integral the band-walking version computed, but
  // in O(1) with no threshold ordering and no divides per band — and since
  // Φ depends only on the deviation, it can be tabulated once per sample
  // over the whole lattice and then read from an array in the inner loop.
  const PHI_NEG = opencutRate *
    (TUNNEL_DEPTH_THRESHOLD * TUNNEL_DEPTH_THRESHOLD - Z_TOLERANCE * Z_TOLERANCE) * 0.5;
  const PHI_POS = PHI_NEG + BASE_COST_PER_KM * 2 * Z_TOLERANCE;
  // Integral of the fill ramp from Z_TOLERANCE up to the bridge threshold —
  // the fill band's counterpart to the opencut term inside PHI_NEG.
  const PHI_FILL = fillRate *
    (FILL_HEIGHT_THRESHOLD * FILL_HEIGHT_THRESHOLD - Z_TOLERANCE * Z_TOLERANCE) * 0.5;
  function Phi(u: number): number {
    if (u > FILL_HEIGHT_THRESHOLD) {
      return PHI_POS + PHI_FILL + BRIDGE_COST_PER_KM * (u - FILL_HEIGHT_THRESHOLD);
    }
    if (u > Z_TOLERANCE) {
      return PHI_POS + fillRate * (u * u - Z_TOLERANCE * Z_TOLERANCE) * 0.5;
    }
    if (u >= -Z_TOLERANCE) return PHI_NEG + BASE_COST_PER_KM * (u + Z_TOLERANCE);
    if (u >= -TUNNEL_DEPTH_THRESHOLD) {
      return opencutRate * (TUNNEL_DEPTH_THRESHOLD * TUNNEL_DEPTH_THRESHOLD - u * u) * 0.5;
    }
    return TUNNEL_COST_PER_KM * (u + TUNNEL_DEPTH_THRESHOLD);
  }

  /** Model cost of an arbitrary profile, for the incumbent bound. */
  function profileCost(zz: number[]): number {
    let c = 0;
    for (let i = 0; i < N - 1; i++) {
      const ds = S[i + 1] - S[i];
      if (ds < 1e-6) continue;
      const d1 = zz[i] - ZT[i], d2 = zz[i + 1] - ZT[i + 1];
      const hug = HUG_EPSILON * (Math.abs(d1) + Math.abs(d2)) * 0.5 * ds;
      if (forcedSeg[i]) { c += BRIDGE_COST_PER_KM * ds + hug; continue; }
      const dd = d2 - d1;
      c += (dd > -1e-12 && dd < 1e-12 ? density(d1) * ds : ds * (Phi(d2) - Phi(d1)) / dd) + hug;
    }
    return c;
  }

  // Mean sample spacing. The lattice pitch is snapped against it so the
  // ruling grade is exactly representable — see inside sweep().
  const dsAvg = (S[N - 1] - S[0]) / Math.max(1, N - 1);

  // --- Sweep --------------------------------------------------------------
  // One full-resolution DP at the requested lattice pitch. `UB` is purely a
  // pruning bound: as long as it is a genuinely achievable cost the result
  // stays exact, so a coarse run's cost can tighten a fine run without
  // constraining where the fine run is allowed to look.
  function sweep(dzIn: number, UB: number): ProfileSolution | null {
    // --- Lattice ------------------------------------------------------------
    // Anchored so that T[0] lands exactly on a level, which lets the start
    // endpoint be pinned to the ground without rounding.
    let dz = dzIn;
    if (relief / dz + 3 > maxLevels) dz = relief / (maxLevels - 3);
    // Snap the pitch so MAX_GRADE * Δs is a whole number of levels. The
    // largest step the lattice can take is floor(MAX_GRADE·Δs/dz) levels, so
    // unless that ratio is an integer the usable ruling grade is quantized
    // DOWN: at dz = 0.557 m with Δs = 0.4 km the ratio is 50.27, and the road
    // can then only descend at 6.96% instead of 7%. On terrain steeper than
    // the ruling grade that shortfall compounds every segment and changes the
    // structure entirely, which is why results jumped with tiny pitch changes.
    const stepKm = MAX_GRADE * dsAvg;
    const wkExact = Math.max(1, Math.round(stepKm / dz));
    dz = stepKm / wkExact;
    const kAnchor = Math.ceil((T[0] - zLo) / dz);
    const zBase = T[0] - kAnchor * dz;
    const M = Math.floor((zHi - zBase) / dz) + 1;
    if (M < 1 || !isFinite(M)) return null;
    const levelZ = (k: number) => zBase + k * dz;
    const kEnd = Math.round((T[N - 1] - zBase) / dz);
    if (kEnd < 0 || kEnd >= M) return null;

    // Per-sample admissible level window: terrain elevation range, intersected
    // with the ruling-grade cones from both pinned endpoints, and held above
    // the bridge deck where the road is over open water.
    const sEnd = S[N - 1];
    const kLo = new Int32Array(N);
    const kHi = new Int32Array(N);
    for (let i = 0; i < N; i++) {
      let lo = zLo, hi = zHi;
      const dStart = MAX_GRADE * (S[i] - S[0]);
      const dFinish = MAX_GRADE * (sEnd - S[i]);
      lo = Math.max(lo, T[0] - dStart, T[N - 1] - dFinish);
      hi = Math.min(hi, T[0] + dStart, T[N - 1] + dFinish);
      if (samples[i].overWater) lo = Math.max(lo, T[i]);
      kLo[i] = Math.max(0, Math.ceil((lo - zBase) / dz - 1e-9));
      kHi[i] = Math.min(M - 1, Math.floor((hi - zBase) / dz + 1e-9));
    }
    kLo[0] = kAnchor; kHi[0] = kAnchor;
    kLo[N - 1] = kEnd; kHi[N - 1] = kEnd;
    for (let i = 0; i < N; i++) if (kLo[i] > kHi[i]) return null;

    // --- Bounds -------------------------------------------------------------
    // Cheapest density any segment can possibly have, for the admissible
    // heuristic. Never optimistic in the wrong direction, so pruning with it
    // cannot discard the optimum.
    const minDensity = Math.min(
      BASE_COST_PER_KM,
      opencutRate * Z_TOLERANCE,
      fillRate * Z_TOLERANCE,   // cheapest point of the fill ramp
      BRIDGE_COST_PER_KM,
      TUNNEL_COST_PER_KM,
    );
    const toGo = new Float64Array(N);
    for (let i = 0; i < N; i++) toGo[i] = minDensity * (sEnd - S[i]);

    // --- Forward sweep ------------------------------------------------------
    // Per-sample tables over the lattice: deviation, |deviation| (for the hug
    // term) and Φ(deviation). Rolled forward one sample at a time.
    let devA = new Float64Array(M), devB = new Float64Array(M);
    let absA = new Float64Array(M), absB = new Float64Array(M);
    let phiA = new Float64Array(M), phiB = new Float64Array(M);
    function fill(i: number, dev: Float64Array, abs: Float64Array, phi: Float64Array) {
      const t = ZT[i];
      for (let k = kLo[i]; k <= kHi[i]; k++) {
        const d = zBase + k * dz - t;
        dev[k] = d;
        abs[k] = d < 0 ? -d : d;
        phi[k] = Phi(d);
      }
    }
    fill(0, devA, absA, phiA);

    let prev = new Float64Array(M).fill(Infinity);
    let cur = new Float64Array(M);
    const parent = new Int32Array(N * M).fill(-1);
    prev[kAnchor] = 0;
    for (let i = 0; i < N - 1; i++) {
      fill(i + 1, devB, absB, phiB);
      const ds = S[i + 1] - S[i];
      const wk = Math.floor((MAX_GRADE * ds) / dz + 1e-9);
      const dZT = ZT[i + 1] - ZT[i];
      const hugK = HUG_EPSILON * 0.5 * ds;
      const forced = forcedSeg[i];
      const forcedCost = BRIDGE_COST_PER_KM * ds;
      cur.fill(Infinity);
      const rowNext = (i + 1) * M;
      const lo2 = kLo[i + 1], hi2 = kHi[i + 1];
      const cap = UB - toGo[i];
      for (let k1 = kLo[i]; k1 <= kHi[i]; k1++) {
        const c1 = prev[k1];
        if (c1 === Infinity || c1 > cap) continue;
        const d1 = devA[k1], a1 = absA[k1], p1 = phiA[k1];
        const kA = k1 - wk > lo2 ? k1 - wk : lo2;
        const kB = k1 + wk < hi2 ? k1 + wk : hi2;
        for (let k2 = kA; k2 <= kB; k2++) {
          const dd = (k2 - k1) * dz - dZT;
          let seg: number;
          if (forced) seg = forcedCost;
          else if (dd > -1e-12 && dd < 1e-12) seg = density(d1) * ds;
          else seg = ds * (phiB[k2] - p1) / dd;
          const c = c1 + seg + hugK * (a1 + absB[k2]);
          if (c < cur[k2]) { cur[k2] = c; parent[rowNext + k2] = k1; }
        }
      }
      // Roll the buffers forward (TS needs these typed one at a time).
      const swC = prev; prev = cur; cur = swC;
      const swD = devA; devA = devB; devB = swD;
      const swA = absA; absA = absB; absB = swA;
      const swP = phiA; phiA = phiB; phiB = swP;
    }
    if (prev[kEnd] === Infinity) return null;

    const z = new Array<number>(N);
    let k = kEnd;
    for (let i = N - 1; i >= 0; i--) {
      z[i] = levelZ(k);
      if (i > 0) {
        const p = parent[i * M + k];
        if (p < 0) return null;
        k = p;
      }
    }
    return { z, cost: prev[kEnd], dzKm: dz };
  }

  // Bound chain: the status-quo profile gives a first incumbent, then a
  // coarse lattice run usually gives a far tighter one for a fraction of the
  // work — which is what makes the full-resolution sweep affordable.
  let bound = seedZ && seedZ.length === N ? profileCost(seedZ) : Infinity;
  // Lattice pitch from a work budget. Transition count is roughly
  // N · (relief/dz) · (2·MAX_GRADE·Δs/dz), so dz scales as the square root
  // of (work / budget). Clamped to [dzKm, dzMaxKm] — coarser than ~1 m
  // starts to blur the ±Z_TOLERANCE at-grade band, which is the mistake the
  // old coarse pass made.
  const work = N * relief * 2 * MAX_GRADE * dsAvg;
  const dzBudget = Math.sqrt(work / (options.transitionBudget ?? 1.5e6));
  const dzFine = Math.min(
    options.dzMaxKm ?? 0.001,
    Math.max(dzWanted, relief / (maxLevels - 3), dzBudget),
  );
  const dzCoarse = Math.max(dzFine, relief / 128);
  if (dzCoarse > dzFine * 1.5) {
    const coarse = sweep(dzCoarse, bound);
    if (coarse) {
      const c = profileCost(coarse.z);
      if (c < bound) bound = c;
    }
  }
  return sweep(dzFine, bound);
}

/**
 * Snap at-grade stretches onto the terrain. The lattice resolves elevation
 * to dzKm, so even after the hug term the road sits on the nearest lattice
 * level rather than exactly on the ground — a residual stairstep of up to
 * half a level. Within a run of at-grade segments, cost is constant, so
 * replacing the profile with the minimum-deviation profile pinned at the
 * run's endpoints is exactly cost-neutral and removes the stairstep.
 *
 * The pinned min-deviation construction (clamp the terrain into the tube
 * spanned by both pins' ruling-grade cones, then forward-clamp) is
 * MAX_GRADE-Lipschitz by construction and hits both pins, so structures on
 * either side are untouched. Moving z toward zT can only shrink |diff|, so
 * at-grade segments stay at-grade; the caller re-checks anyway and reverts
 * the run if any kind changed.
 */
function hugTerrainOnGradeRuns(samples: Sample[], z: number[], kinds: Kind[]): void {
  let i = 0;
  while (i < kinds.length) {
    if (kinds[i] !== "grade") { i++; continue; }
    const a = i;
    while (i < kinds.length && kinds[i] === "grade") i++;
    const b = i;                       // samples a..b, segments a..b-1
    if (b - a < 2) continue;           // single segment: endpoints are pinned
    const sA = samples[a].s, sB = samples[b].s;
    const zA = z[a], zB = z[b];
    const saved = z.slice(a, b + 1);
    for (let k = a + 1; k < b; k++) {
      const s = samples[k].s;
      const U = Math.min(zA + MAX_GRADE * (s - sA), zB + MAX_GRADE * (sB - s));
      const L = Math.max(zA - MAX_GRADE * (s - sA), zB - MAX_GRADE * (sB - s));
      const y = Math.min(U, Math.max(L, samples[k].zT));
      const dx = s - samples[k - 1].s;
      z[k] = Math.min(z[k - 1] + MAX_GRADE * dx, Math.max(z[k - 1] - MAX_GRADE * dx, y));
    }
    // Revert if any segment in the run left the at-grade band.
    let ok = true;
    for (let k = a; k < b && ok; k++) {
      const diff = (z[k] + z[k + 1]) * 0.5 - (samples[k].zT + samples[k + 1].zT) * 0.5;
      if (diff > Z_TOLERANCE || diff < -Z_TOLERANCE) ok = false;
    }
    if (!ok) for (let k = a; k <= b; k++) z[k] = saved[k - a];
  }
}

/**
 * Explicit deck geometry for every bridge run: where each span starts and
 * ends, in arc length and elevation.
 *
 * Two things go wrong if the renderer infers this from the kinds array, as
 * the profile strip did — it drew a straight chord from the first to the
 * last SAMPLE of each bridge run:
 *
 *   Buried abutments. classifySegments labels a segment from its MEAN
 *   deviation, so a segment running from 271 m underground up to well above
 *   it averages positive and is labelled "bridge". Its left sample is 271 m
 *   below the terrain, and a deck drawn from that sample starts underground.
 *   Measured over 16 roads on 4 days: 194 of 318 bridge runs began or ended
 *   at a buried sample. This is the "bridge goes through underground" case,
 *   and it is a labelling artifact rather than anything the profile does —
 *   the road really is emerging from a tunnel mouth partway along that
 *   segment. So the abutment belongs at the ground CROSSING inside the
 *   segment, not at the sample.
 *
 *   Arched spans. Where the scored profile bows over a rise, the chord
 *   between abutments can pass below it even though every sample has
 *   clearance. Rarer — 6 of 318 — and fixed by splitting at the worst
 *   violation and recursing, which is a deck on an intermediate pier.
 *
 * Elevations come from the profile, so the spans always agree with the
 * scored z. Nothing here changes cost; it is the geometry the renderer
 * should have been given in the first place.
 */
export type DeckSpan = { s0: number; z0: number; s1: number; z1: number };

export function bridgeDeckSpans(
  samples: Sample[], z: number[], kinds: Kind[],
): DeckSpan[] {
  const out: DeckSpan[] = [];

  /** Arc position where the road crosses terrain inside segment i..i+1. */
  function crossing(i: number): { s: number; z: number } | null {
    const d0 = z[i] - samples[i].zT, d1 = z[i + 1] - samples[i + 1].zT;
    if ((d0 <= 0) === (d1 <= 0)) return null;      // no sign change
    const t = d0 / (d0 - d1);
    return {
      s: samples[i].s + (samples[i + 1].s - samples[i].s) * t,
      z: z[i] + (z[i + 1] - z[i]) * t,
    };
  }

  const emit = (s0: number, z0: number, s1: number, z1: number,
                iLo: number, iHi: number) => {
    if (!(s1 > s0)) return;
    // Split at the worst point where the chord dips below terrain.
    let worst = 0, at = -1;
    for (let k = iLo; k <= iHi; k++) {
      const sk = samples[k].s;
      if (sk <= s0 || sk >= s1) continue;
      const deck = z0 + (z1 - z0) * ((sk - s0) / (s1 - s0));
      const below = samples[k].zT - deck;
      if (below > worst) { worst = below; at = k; }
    }
    if (at < 0) { out.push({ s0, z0, s1, z1 }); return; }
    emit(s0, z0, samples[at].s, z[at], iLo, at);
    emit(samples[at].s, z[at], s1, z1, at, iHi);
  };

  let i = 0;
  while (i < kinds.length) {
    if (kinds[i] !== "bridge") { i++; continue; }
    const a = i;
    while (i < kinds.length && kinds[i] === "bridge") i++;
    const b = i;                                  // samples a..b

    // Abutments: the ground crossing inside the boundary segment when the
    // run's own end sample is buried, otherwise the sample itself.
    let s0 = samples[a].s, z0 = z[a], iLo = a;
    if (z[a] < samples[a].zT) {
      const c = crossing(a);
      if (c) { s0 = c.s; z0 = c.z; iLo = a + 1; }
    }
    let s1 = samples[b].s, z1 = z[b], iHi = b;
    if (z[b] < samples[b].zT) {
      const c = crossing(b - 1);
      if (c) { s1 = c.s; z1 = c.z; iHi = b - 1; }
    }
    emit(s0, z0, s1, z1, iLo, iHi);
  }
  return out;
}

/** Arc positions where the structure kind changes — portal mouths, bridge
 *  abutments, and at-grade transitions — for rendering. */
function portalsFromKinds(samples: Sample[], kinds: Kind[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < kinds.length; i++) {
    if (kinds[i] !== kinds[i - 1]) out.push(samples[i].s);
  }
  return out;
}

/**
 * Options for the profile refinement stage.
 *
 * solveProfileOptimal dominates this function's runtime — 214 of 219 ms on a
 * 341-sample road, because its lattice is N x 1600 levels with ~57
 * transitions per state, about 31 million operations. Every other stage
 * combined is under 5 ms.
 *
 * It is a pure improvement: the result is discarded unless it beats the
 * baseline profile on cost. So coarsening or skipping it can only make the
 * reported cost higher, never lower — which makes it safe for search, where
 * what matters is the ranking between routes, not the last dollar.
 */
export type AnalyzeOptions = {
  /** Skip the optimal-profile refinement entirely. ~40x faster. */
  fastProfile?: boolean;
  /** Lattice levels for the refinement. Default 1600; 200 is ~8x faster. */
  maxLevels?: number;
};

export function analyzeRoad(
  road: Road, puzzle: Puzzle, options: AnalyzeOptions = {},
): Analysis | null {
  const samples = buildSampledRoad(road, puzzle.params);
  if (!samples) return null;
  // Before solving the profile, lift any samples that fall inside a lake
  // to the lake's surface elevation. This makes the road profile sit on
  // top of the lake rather than dipping into the basin floor.
  liftSamplesForLakes(samples, puzzle.lakes);
  const { z: z0, feasible } = solveProfile(samples);
  // Per-segment geometric tests for river crossings and lake passages.
  // The resulting boolean[] overrides any natural classification with
  // "bridge" in classifySegments below.
  const forceBridge = computeWaterCrossings(samples, puzzle.rivers, puzzle.lakes);

  // Baseline: the status-quo alignment from solveProfile.
  const kinds0 = classifySegments(samples, z0, forceBridge);
  const speeds0 = computeVelocityProfile(samples, kinds0);
  const score0 = scoreRoute(samples, z0, kinds0, speeds0);
  if (!score0) return null;

  // Globally optimal profile. Kept only if it beats the baseline under the
  // real scoring function and respects the ruling grade, so this can never
  // make a score worse.
  const opt = options.fastProfile
    ? null
    : solveProfileOptimal(
        samples, forceBridge,
        options.maxLevels !== undefined ? { maxLevels: options.maxLevels } : {},
        z0,
      );
  if (opt) {
    // Polish before classifying: the snap is cost-neutral within at-grade
    // runs, so kinds are unaffected, but it removes the lattice stairstep.
    hugTerrainOnGradeRuns(samples, opt.z, classifySegments(samples, opt.z, forceBridge));
    const kinds1 = classifySegments(samples, opt.z, forceBridge);
    const speeds1 = computeVelocityProfile(samples, kinds1);
    const score1 = scoreRoute(samples, opt.z, kinds1, speeds1);
    let gradeOk = true;
    for (let i = 1; i < samples.length; i++) {
      const ds = samples[i].s - samples[i - 1].s;
      if (ds < 1e-9) continue;
      if (Math.abs((opt.z[i] - opt.z[i - 1]) / ds) > MAX_GRADE + 1e-6) {
        gradeOk = false;
        break;
      }
    }
    if (score1 && gradeOk && score1.cost < score0.cost - 1e-9) {
      return {
        samples, z: opt.z, kinds: kinds1, speeds: speeds1,
        score: score1, feasible, portals: portalsFromKinds(samples, kinds1),
        deckSpans: bridgeDeckSpans(samples, opt.z, kinds1),
      };
    }
  }
  return {
    samples, z: z0, kinds: kinds0, speeds: speeds0,
    score: score0, feasible, portals: portalsFromKinds(samples, kinds0),
    deckSpans: bridgeDeckSpans(samples, z0, kinds0),
  };
}
